import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';

import { token } from '@bp-files/ui';
import {
  clampPdfScale,
  deviceScale,
  normalizeRotation,
  rotatedSize,
  type Size,
} from './pdfPageLayout';
import {
  IDENTITY,
  anchorIn,
  midpoint,
  pinchRatio,
  pinchView,
  scrollCorrection,
  spread,
  type Point,
  type Rect,
  type View,
} from './pinchZoom';
import type { PdfDocumentHandle, PdfPageHandle } from './pdfDocument';

/**
 * The pages, in a column that scrolls (FIL-8b).
 *
 * **Scroll is the pan, and re-rendering is the zoom.** `ZoomStage` translates
 * and scales one decoded bitmap because that is the whole of what a photograph
 * can offer. A document has more detail than any single rasterization holds, so
 * changing the scale here changes the size of the canvas a page is drawn into
 * and draws it again. That is the difference between reading a filing at 400%
 * and looking at a blurred picture of one, and it is why FIL-8b states it as a
 * requirement rather than leaving it to this file.
 *
 * **A live pinch is the one exception, and it proves the rule** (ADR-0101).
 * While two fingers are down the content box is scaled with a transform and
 * nothing is re-rendered; the scale commits when the fingers lift and the pages
 * are drawn again. The requirement above is about the page **at rest**, and the
 * reason to bend it is not smoothness for its own sake: re-rendering per touch
 * sample cancelled every render before it finished *and* re-sized the canvas,
 * which blanks it, so the old behaviour showed a blank page rather than a sharp
 * one. A blank page is not a more faithful document than a briefly soft one.
 * `pinchZoom.ts` holds the arithmetic and the invariant is simple: **identity
 * whenever no gesture is running.**
 *
 * **The gesture is taken off the browser before any of that matters.** A
 * `touch-action` declaration stops Chrome page-zooming but leaves a two-finger
 * *pan* the browser is entitled to run, and iOS Safari has never honoured
 * `touch-action` for pinch at all. Both are refused by `preventDefault` on a
 * non-passive `touchmove`, for two fingers only — see `claim` below, which is
 * the difference between this working on a device and not working at all.
 *
 * **Three more rules keep the gesture attached to the fingers**, and each one
 * cost a measured bug (ADR-0101 §4). The transform is anchored at the content box's
 * corner, never its centre, which is CSS's default. Its untransformed origin is
 * derived from the box rather than from `scrollTop`, because a transform feeds
 * the scroll container's overflow and the browser clamps the offset underneath
 * you — hardest when zooming *out*. And both fingers are read once per frame,
 * never per `pointermove`, because a touchscreen reports one event per finger
 * and half of them carry a stale partner.
 *
 * **Only pages near the viewport hold pixels.** A letter page at 500% on a
 * 2× display is about 24 megapixels and 97 MB of canvas; a discovery packet is
 * hundreds of pages. Each page keeps its measured box at every scale — so the
 * scrollbar is honest and nothing jumps — but its canvas is drawn only within
 * `RENDER_MARGIN` of the viewport and is released, by setting its dimensions to
 * zero, when it goes out of range. Clearing the reference alone is not enough:
 * on iOS Safari the backing store outlives it.
 *
 * **Every render is cancellable and every *committed* scale change cancels.** A
 * scroll outruns the renderer easily, and two renders onto one canvas do not
 * throw — they interleave, and page four's content ends up over page five's. A
 * gesture cancels nothing, because it changes no scale until it ends.
 */

/**
 * How far outside the viewport a page is still worth holding pixels for.
 *
 * Expressed in percentages of the root rather than pixels, which is both the
 * better behaviour — a tall window keeps more pages warm than a short one —
 * and what keeps this off the raw-px rule.
 */
const RENDER_MARGIN = '150% 0%';

/** The gap between pages, and the breathing room either side of them. */
const GUTTER = 24;

/**
 * How far from 1 a pinch's ratio has to land before it counts as a zoom.
 *
 * Two fingers resting on a page is a way of holding a phone, and a gesture that
 * never moved should leave the reader exactly where they were — `fitMode`
 * included.
 */
const MOVED = 0.01;

/**
 * How long after the fingers leave before the pages are drawn again.
 *
 * **Deliberately long enough to notice.** `ZoomStage` waits 150ms for a
 * different reason — a wheel has no end event, so the timer is how it learns
 * the gesture is over. A pinch says exactly when it ends, so this is not
 * detection: it is a refusal to sharpen between the halves of one movement. A
 * reader zooming a filing rarely does it in a single spread; they pinch, let
 * go, adjust, pinch again. Committing on each lift spent a full re-render on a
 * scale that was about to be replaced, and re-rendering is the one thing here
 * that is not free.
 *
 * A finger returning inside this window cancels it and carries on from the
 * transform already applied, so the whole sequence costs one draw.
 */
const SETTLE_MS = 260;

/**
 * The canvas budget for one page.
 *
 * pdf.js's own default, and the number iOS Safari starts silently returning
 * blank canvases somewhere above.
 */
const MAX_CANVAS_PIXELS = 16_777_216;

export interface PdfStageProps {
  document: PdfDocumentHandle;
  /**
   * The resolved scale, never a fit mode.
   *
   * Working out what "fit" means needs the stage's measured size and a page's
   * dimensions, and the frame already holds both — so it resolves the number
   * and this component only ever draws at one. Passing the mode down would put
   * the same arithmetic in two places and make "what scale am I at" a question
   * with two answers.
   */
  scale: number;
  /** What the reader has turned the document by, on top of each page's own. */
  rotation: number;
  /** Reports the measured stage, so the frame can work out what fit means. */
  onStageResize: (size: Size) => void;
  /** Reports the page most in view, one-based. */
  onPageChange: (page: number) => void;
  /** Set by the frame when it wants a page brought into view. */
  scrollToPage: number | undefined;
  /**
   * A two-finger pinch, as an absolute scale for the frame to clamp and adopt.
   *
   * **This is the zoom on a phone, and it is this stage's rather than the
   * browser's — which is a reversal.** Leaving it to the browser meant leaving
   * it to *page* zoom, which magnifies the visual viewport: the toolbar, the
   * bottom bar and the filename all grew with the document, and the chrome
   * drifted off the edges of a screen that had no room to spare. Chrome is
   * furniture; the page is the thing being read, and only one of them should
   * answer a pinch.
   *
   * So the container below takes `touch-action: pan-x pan-y` — panning kept,
   * the browser's pinch declined — and this reports the gesture instead. It is
   * an absolute scale rather than a delta so the frame stays the single place
   * that knows what scale is being drawn at, exactly as `scale` above says.
   *
   * **Called once per gesture, not once per move** (ADR-0101). It used to fire
   * on every `pointermove`, and that is the whole of what made a pinch jerky —
   * each call re-sized every near page's canvas, which blanks it, and cancelled
   * the render that was drawing into it. The gesture is now magnified with a
   * transform this component writes itself, and this reports the scale the
   * reader settled on when a finger lifts. Anything that re-introduces a call
   * per move re-introduces the bug.
   *
   * **`pan-x pan-y`, never `none`.** The document has to keep scrolling under a
   * finger, and `touch-action` is not inherited: `FieldLayer` stays `auto`, as
   * its header requires and `e2e/signing.mobile.spec.ts` asserts.
   */
  onPinch?: (scale: number) => void;
  /**
   * FIL-8c's field overlay, rendered above each page.
   *
   * A render prop rather than a component this file imports, so the stage keeps
   * knowing nothing about signing — it is a reader, and a reader that had to be
   * told what an overlay is would be one more thing to change when a second one
   * appears. It is handed the page's index and the view the fields' arithmetic
   * needs, both of which only this component knows.
   */
  renderOverlay?: (
    pageIndex: number,
    view: { scale: number; rotation: number; page: Size; gesturing: boolean },
  ) => ReactNode;
}

export function PdfStage({
  document: pdf,
  scale,
  rotation,
  onStageResize,
  onPageChange,
  scrollToPage,
  onPinch,
  renderOverlay,
}: PdfStageProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef(new Map<number, HTMLDivElement>());

  const scaleRef = useRef(scale);

  /**
   * The gesture so far, as a transform relative to the scale being drawn at.
   *
   * **It outlives the fingers.** `IDENTITY` means no gesture is pending; it is
   * set on the first sample and reset only when the scale commits, which is
   * `SETTLE_MS` after the last finger leaves. That gap is what lets a reader
   * pinch, adjust, and pinch again as one movement — each new gesture picks up
   * the transform the last one left instead of forcing a re-render between
   * them.
   */
  const view = useRef<View>(IDENTITY);

  /**
   * The box the gesture magnifies, written to **directly** rather than through
   * state.
   *
   * **This is the second half of the fix and it is easy to miss.** Keeping the
   * live scale out of `PdfViewer` stops pdf.js being cancelled, but a `useState`
   * here would still re-render one `PdfPage` element per page in the document —
   * every page, not only the near ones — on every frame of the gesture, each one
   * recomputing its box and re-invoking `renderOverlay`. On a long filing that
   * stutters on its own, with the canvases entirely innocent. A transform is a
   * compositor property and needs no render to change, so the gesture never
   * enters React at all.
   */
  const contentRef = useRef<HTMLDivElement>(null);

  /**
   * Whether a pinch is in flight — the one piece of the gesture React does see,
   * and it changes exactly twice per gesture rather than once per frame.
   *
   * It exists for `FieldLayer`: a drag pinned to one finger stays live when a
   * second finger lands anywhere on the stage, and its screen→points arithmetic
   * divides by the committed scale, which a running transform has made a lie.
   */
  const [gesturing, setGesturing] = useState(false);

  /**
   * Read by the pages' `IntersectionObserver`s, which see through a transform.
   *
   * Magnifying the column moves every page's intersection rect, so a page can
   * be demoted mid-gesture — and demotion sets `canvas.width = 0`, which is the
   * blanking this whole change exists to remove, arriving by a second route.
   * A ref rather than the state above so the observers never re-bind.
   */
  const gestureRef = useRef(false);

  /**
   * What the committed scale still owes a scroll adjustment for: a point on a
   * named page, and where on screen it has to end up.
   *
   * **A page and a fraction of it, never a predicted offset.** The column does
   * not scale uniformly — the gap between pages is 24px at every zoom — so
   * arithmetic that multiplied the old scroll by the gesture's ratio was out by
   * one gutter per page, which is how a commit used to land somewhere the
   * reader had not asked for. The layout effect below measures the page instead,
   * after the browser has laid it out and already accounted for the gaps.
   */
  const pendingAnchor = useRef<
    { pageIndex: number; anchor: ReturnType<typeof anchorIn>; focus: Point } | undefined
  >(undefined);

  // Written in an effect rather than during render: `react-hooks/refs` is right
  // that a ref touched while rendering is a value the renderer cannot see
  // change. Nothing reads this one during render either — it exists so the
  // pinch listeners can be bound once instead of re-bound on every zoom frame.
  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  /** Each page's unrotated size and own `/Rotate`, once it has been asked for. */
  const [pages, setPages] = useState<ReadonlyMap<number, PdfPageHandle>>(() => new Map());

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || typeof ResizeObserver === 'undefined') return;

    const report = () => {
      onStageResize({ width: stage.clientWidth, height: stage.clientHeight });
    };

    report();
    const observer = new ResizeObserver(report);
    observer.observe(stage);

    return () => {
      observer.disconnect();
    };
  }, [onStageResize]);

  /**
   * Ask the document for every page's dimensions once.
   *
   * Cheap — `getPage` parses the page dictionary, not its content — and it is
   * what lets every page have a correctly sized box before any of them is
   * drawn. Without it the column grows as pages render and the scrollbar moves
   * under the reader's hand.
   */
  useEffect(() => {
    let live = true;

    async function measure(): Promise<void> {
      const found = new Map<number, PdfPageHandle>();
      for (let index = 0; index < pdf.pageCount; index += 1) {
        const page = await pdf.getPage(index);
        if (!live) return;
        found.set(index, page);
      }
      if (live) setPages(found);
    }

    void measure();

    return () => {
      live = false;
    };
  }, [pdf]);

  /** Which page is most in view, reported up so the footer can say so. */
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || typeof IntersectionObserver === 'undefined') return;

    const visible = new Map<number, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const index = Number((entry.target as HTMLElement).dataset['pageIndex']);
          visible.set(index, entry.intersectionRatio);
        }

        let best = -1;
        let bestRatio = 0;
        for (const [index, ratio] of visible) {
          if (ratio > bestRatio) {
            best = index;
            bestRatio = ratio;
          }
        }

        if (best >= 0) onPageChange(best + 1);
      },
      { root: stage, threshold: [0, 0.1, 0.5, 0.9, 1] },
    );

    for (const element of pageRefs.current.values()) observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [onPageChange, pages]);

  useEffect(() => {
    if (scrollToPage === undefined) return;
    pageRefs.current.get(scrollToPage - 1)?.scrollIntoView({ block: 'start' });
  }, [scrollToPage]);

  /**
   * The pinch, listened for on the whole stage.
   *
   * **Capture-phase native listeners, not React's `onPointerDown` and
   * friends.** `FieldLayer`'s stamps, its resize handle and its text box all
   * `stopPropagation()` on pointerdown so a drag is theirs alone — perfectly
   * right for a drag, and it would hide the second finger of a pinch that
   * happened to start on a placed signature. Capture runs on the way down,
   * before any of them.
   *
   * Two pointers exactly. A third is somebody resting a palm, and re-basing the
   * gesture on it would make the page jump; the ratio simply keeps using the
   * first two.
   */
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || onPinch === undefined) return;

    const active = new Map<number, Point>();
    /** The two fingers as the last drawn frame saw them. */
    let last: { spread: number; mid: Point } | undefined;
    /** Whether two fingers are down now, as opposed to settling. */
    let pinching = false;
    let frame: number | undefined;
    let settle: ReturnType<typeof setTimeout> | undefined;
    /** Where the fingers were last seen, so the commit knows what to hold. */
    let focus: Point = { x: 0, y: 0 };

    const fingers = (): [Point, Point] | undefined => {
      const [a, b] = Array.from(active.values());
      if (a === undefined || b === undefined) return undefined;
      return [a, b];
    };

    /** A client point, measured from the stage's top-left corner. */
    const onStage = (point: Point): Point => {
      const rect = stage.getBoundingClientRect();
      return { x: point.x - rect.left, y: point.y - rect.top };
    };

    /**
     * Fold every sample since the last frame into the gesture, and draw it.
     *
     * **The accumulation belongs here rather than in the move handler, and that
     * is not only about doing less work.** A touchscreen reports one
     * `pointermove` per finger, so half of all samples arrive with one finger
     * updated and the other still where it was a frame ago. Accumulating on
     * each of those scales the column about a midpoint that is half a movement
     * out, and the error compounds — measured at 208px of drift over a single
     * 3.3x pinch. Reading both fingers once per frame is the only moment they
     * are ever consistent with each other.
     */
    const paint = () => {
      frame = undefined;

      const content = contentRef.current;
      if (content === null) return;

      const pair = fingers();
      if (pair !== undefined) {
        const now = { spread: spread(pair[0], pair[1]), mid: midpoint(pair[0], pair[1]) };
        focus = onStage(now.mid);

        if (last !== undefined) {
          /*
            Held against the limits every frame, so the pages stop growing
            exactly where the committed scale would stop rather than running
            past it and snapping back. `MIN_SCALE`/`MAX_SCALE` stay
            `pdfPageLayout`'s to know; this asks it the question the frame will.
          */
          const drawn = scaleRef.current * view.current.scale;
          const reachable = clampPdfScale(drawn * pinchRatio(now.spread, last.spread)) / drawn;

          // The anchor is the fingers, expressed from the content box's own
          // untransformed corner — see `contentOrigin` for why that corner is
          // derived from the box and never from the scroll offset.
          const origin = contentOrigin(stage, content, view.current);

          view.current = pinchView(
            view.current,
            { x: focus.x - origin.x, y: focus.y - origin.y },
            reachable,
            { x: now.mid.x - last.mid.x, y: now.mid.y - last.mid.y },
          );
        }

        last = now;
      }

      const { scale: magnified, x, y } = view.current;
      content.style.transform = `translate3d(${String(x)}px, ${String(y)}px, 0) scale(${String(magnified)})`;
    };

    /** Hand the layer back to the rasterizer and leave no transform behind. */
    const rest = () => {
      const content = contentRef.current;
      if (content === null) return;

      content.style.transform = '';
      content.style.willChange = '';
    };

    /**
     * Adopt the gesture, draw the pages again, and put the anchor back.
     *
     * Runs `SETTLE_MS` after the fingers leave rather than the instant they do.
     * That delay is meant to be felt: a pinch is often really three pinches in
     * a row, and committing on each one spent a re-render sharpening something
     * the next movement immediately threw away.
     */
    const commit = () => {
      settle = undefined;
      if (frame !== undefined) {
        cancelAnimationFrame(frame);
        frame = undefined;
      }

      const target = clampPdfScale(scaleRef.current * view.current.scale);

      // Measured now rather than when the fingers left: the column can be
      // scrolled during the settle, and what the reader is holding is whatever
      // sits under that point on screen at this moment.
      const held = anchorPage(stage, pageRefs.current, focus);

      view.current = IDENTITY;
      last = undefined;
      gestureRef.current = false;
      setGesturing(false);

      /*
        **A gesture that resized nothing is not a zoom.** Two fingers resting on
        a page is a way of holding a phone, and reporting the scale it was
        already at would clear `fitMode`, so a fitted document would quietly
        stop following the window. Nothing will re-lay out in that case, so this
        is also the one path that has to undo the transform itself.
      */
      if (held === undefined || Math.abs(target / scaleRef.current - 1) < MOVED) {
        rest();
        return;
      }

      /*
        **The transform is deliberately left on the element here.** Stripping it
        at this point and letting the re-render land whenever it lands makes
        three things — dropping the magnification, laying the pages out at the
        new scale, and correcting the scroll — into three separate paints that
        only *usually* coalesce. When they do not, the frame in between shows the
        document at its pre-pinch size and offset, which is the reader watching
        the zoom go somewhere else and come back.

        The layout effect below does all three inside one commit, before any of
        them can be painted. It reads `pendingAnchor` to know it is owed.
      */
      pendingAnchor.current = { ...held, focus };
      onPinch?.(target);
    };

    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType !== 'touch') return;
      active.set(event.pointerId, { x: event.clientX, y: event.clientY });

      const pair = fingers();
      if (pair === undefined) return;

      // A second finger arriving inside the settle window continues the gesture
      // the last one left, so the pages never sharpen mid-movement.
      if (settle !== undefined) {
        clearTimeout(settle);
        settle = undefined;
      }

      // Seeded here and nowhere else in the event path. Both fingers are
      // current at the moment the second one lands, so this reading is
      // consistent; the per-finger `pointermove`s that follow are not, which is
      // why they only ever schedule a frame.
      last = { spread: spread(pair[0], pair[1]), mid: midpoint(pair[0], pair[1]) };
      pinching = true;
      gestureRef.current = true;
      setGesturing(true);

      // Set once and dropped at rest: a composited layer is rasterized at the
      // size it had when it was made, so a `will-change` left in place would
      // leave the document magnified instead of drawn.
      if (contentRef.current !== null) contentRef.current.style.willChange = 'transform';
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!active.has(event.pointerId)) return;
      active.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (!pinching || active.size < 2) return;
      if (frame === undefined) frame = requestAnimationFrame(paint);
    };

    const onPointerUp = (event: PointerEvent) => {
      active.delete(event.pointerId);

      // Ends on the *first* finger lifting, not the last. Re-basing on a single
      // remaining pointer would turn the lift into a pan of the zoom.
      if (active.size >= 2 || !pinching) return;

      pinching = false;
      last = undefined;
      settle ??= setTimeout(commit, SETTLE_MS);
    };

    /**
     * Take the two-finger gesture off the browser, which is the only way to
     * actually have it.
     *
     * **`touch-action` is not enough, and believing it was is why the pinch did
     * not work on a real phone at all.** The declaration on the stage stops
     * Chrome page-zooming, but two fingers dragging is still a *pan* it is
     * entitled to run — and once the compositor starts one it takes the
     * pointers with it and sends `pointercancel`, so the gesture below ends
     * before it has measured anything. On iOS Safari it is worse:
     * `touch-action` has never covered pinch there, so the browser zooms the
     * visual viewport instead. That is the whole symptom list from a device —
     * the chrome magnifying along with the page, a document that jumps, and
     * pinching *out* doing nothing at all because the page is already at its
     * minimum page zoom and there is nothing below it to go to.
     *
     * `preventDefault` on a **non-passive** touch listener is the only thing
     * either engine accepts, which is also why the pointer listeners above
     * cannot do it: they are passive by choice, because a passive listener is
     * what keeps one-finger scrolling on the compositor thread.
     *
     * **Two fingers exactly, and never one.** A single touch is a scroll or a
     * tap and both belong to the browser — `touch-action: pan-x pan-y` says so
     * and `FieldLayer` depends on it. This claims the multi-touch case and
     * nothing else.
     */
    const claim = (event: TouchEvent) => {
      if (event.touches.length < 2) return;
      if (event.cancelable) event.preventDefault();
    };

    const options = { capture: true, passive: true } as const;
    const owned = { capture: true, passive: false } as const;
    stage.addEventListener('pointerdown', onPointerDown, options);
    stage.addEventListener('pointermove', onPointerMove, options);
    stage.addEventListener('pointerup', onPointerUp, options);
    stage.addEventListener('pointercancel', onPointerUp, options);
    stage.addEventListener('touchstart', claim, owned);
    stage.addEventListener('touchmove', claim, owned);

    return () => {
      if (frame !== undefined) cancelAnimationFrame(frame);
      if (settle !== undefined) clearTimeout(settle);
      view.current = IDENTITY;
      gestureRef.current = false;
      rest();
      stage.removeEventListener('pointerdown', onPointerDown, options);
      stage.removeEventListener('pointermove', onPointerMove, options);
      stage.removeEventListener('pointerup', onPointerUp, options);
      stage.removeEventListener('pointercancel', onPointerUp, options);
      stage.removeEventListener('touchstart', claim, owned);
      stage.removeEventListener('touchmove', claim, owned);
    };
  }, [onPinch]);

  /**
   * Put the anchored point back under the fingers.
   *
   * **A layout effect rather than an effect**, and keyed on the committed
   * `scale` so it runs once the new page boxes exist but before the frame is
   * painted. In a plain effect the reader gets one frame at the old offset,
   * which reads as the document flinching at the end of every pinch.
   *
   * **The page is measured, never predicted.** The gaps between pages do not
   * scale, so a document at 3x is three times as tall *less* the gutters that
   * stayed 24px — 8598px where the arithmetic said 8838 on a six-page filing,
   * and the error grows with every page above the one being held. Reading the
   * rect the browser has just produced is the only version of this that is
   * right for a page anywhere but the first.
   */
  useLayoutEffect(() => {
    const stage = stageRef.current;
    const owed = pendingAnchor.current;
    if (!stage || owed === undefined) return;

    pendingAnchor.current = undefined;

    /*
      **Dropped here, and the order below is the whole point.** The pages have
      just been laid out at the committed scale and the magnification is still
      on top of them; both are true only inside this effect, and neither has
      been painted yet. Clearing the transform first is what makes the
      measurement that follows a reading of the *new* layout rather than of the
      new layout seen through the old gesture.
    */
    const content = contentRef.current;
    if (content !== null) {
      content.style.transform = '';
      content.style.willChange = '';
    }

    const box = pageRefs.current.get(owed.pageIndex);
    if (box === undefined) return;

    const delta = scrollCorrection(rectOnStage(stage, box), owed.anchor, owed.focus);

    // Left to the browser to clamp: it is the one that knows what the new
    // layout allows, and a document narrower than the stage has no horizontal
    // scroll to give at all.
    stage.scrollLeft += delta.x;
    stage.scrollTop += delta.y;
    /*
      No dependency list, because there is no value that reliably changes. A
      pinch can land on exactly the scale a fit mode was already resolving to —
      the state it writes still differs, so the component re-renders, but
      `scale` does not, and keying on it would leave the transform on the
      element for good. The ref check above is the guard instead, and it costs
      one comparison per render.
    */
  });

  const registerPage = useCallback((index: number, element: HTMLDivElement | null) => {
    if (element === null) pageRefs.current.delete(index);
    else pageRefs.current.set(index, element);
  }, []);

  return (
    <div
      ref={stageRef}
      data-testid="pdf-stage"
      /*
        Nothing here may introduce an offscreen buffer — no filter, no
        backdrop-filter, no opacity below 1, no blend mode, on this element or
        any wrapper above a page. `ZoomStage`'s comment says the same for the
        same reason: an intermediate surface is resampled, and resampling is the
        softness this stage re-renders to avoid.
      */
      className="min-h-0 flex-1 overflow-auto"
      style={{
        padding: GUTTER,
        background: token('bp-app'),
        /*
          **Chrome adjusts this container's scroll offset by itself, and it has
          to be told not to.** Scroll anchoring watches for content above the
          viewport changing size and silently compensates — which is exactly
          what a committed zoom does to every page above the one being read. It
          picks its own anchor element, so its correction and the focal-point
          correction below both fire and the document lands thousands of pixels
          from where the reader was holding it. Measured before this line: a
          3.3x pinch landed 3324px out. One of the two has to own the offset,
          and only one of them knows where the fingers were.
        */
        overflowAnchor: 'none',
        /*
          **`pan-x pan-y`: keep the scroll, decline the browser's pinch.** Page
          zoom magnifies the visual viewport, so a pinch here used to grow the
          toolbar and the bottom bar along with the document — chrome is
          furniture and only the page should answer the gesture. The pinch is
          handled above instead.

          **Never `none`.** The document must still scroll under one finger.
          `touch-action` is not inherited, so `FieldLayer` stays `auto`, which
          its header requires and `e2e/signing.mobile.spec.ts` asserts.

          **On the scroller, not on the box below it.** The wrapper is what the
          gesture transforms, and a `touch-action` that travelled with it would
          be a different declaration every time somebody restructured this.
        */
        touchAction: 'pan-x pan-y',
      }}
    >
      <div
        ref={contentRef}
        data-testid="pdf-stage-content"
        /*
          **The box the pinch magnifies, and the only thing that is ever
          magnified.** While two fingers are down this is scaled on the
          compositor, so the pages track the gesture without a single
          re-render; the moment the scale commits the transform is gone and
          pdf.js has drawn the pages again at the size asked for. FIL-8b's rule
          is about the page a reader is looking at, and a resting page here is
          always a drawn one — see ADR-0101.

          **`w-max min-w-full`, and it is not decoration.** `items-center` in a
          scroll container centres an oversized child by overflowing it equally
          both ways, and the half that overflows to the left is unreachable —
          scrolling cannot go below zero. Sizing this box to its widest page
          instead means the centring happens inside a box that is never
          narrower than its contents, so a page zoomed past the width of the
          screen can be read from its left edge.

          The same prohibition as the scroller above: nothing here may
          introduce an offscreen buffer.
        */
        className="flex w-max min-w-full flex-col items-center"
        /*
          No `transform` here, and that is deliberate rather than an omission:
          the gesture writes it straight to this element, so React never
          re-renders a page while fingers are moving. Runtime-computed geometry
          either way — the exemption `ZoomStage`'s header documents.
        */
        style={{
          /*
            **The gap scales with the zoom, and it has to.** A transform scales
            everything inside this box including the space between pages; the
            layout that replaces it at the commit scales only the pages. A
            constant gutter therefore means the two disagree by one gutter per
            page, and the reader watches the document above the point they were
            holding snap by that much the moment it sharpens — measured at 168px
            on a 3.3x pinch. Scaling it makes the magnified column and the drawn
            column the same shape, so the commit changes the pixels and nothing
            about where anything is. It is also what a browser's own PDF viewer
            does: the space between pages grows with the page.
          */
          gap: GUTTER * scale,
          /*
            **`0 0`, and every number in `pinchZoom.ts` depends on it.** The CSS
            default is the box's centre, which makes a scale move the corner as
            well — so the translation the gesture accumulates and the position
            the browser actually draws at drift apart, and the further the
            reader is into the document the worse it gets. Anchoring at the
            corner is what makes "a content point sits at origin + x + scale·p"
            true, which is the one line the whole gesture is derived from.
          */
          transformOrigin: '0 0',
        }}
      >
        {Array.from({ length: pdf.pageCount }, (_, index) => (
          <PdfPage
            key={index}
            index={index}
            page={pages.get(index)}
            scale={scale}
            rotation={rotation}
            register={registerPage}
            gesture={gestureRef}
            gesturing={gesturing}
            renderOverlay={renderOverlay}
          />
        ))}
      </div>
    </div>
  );
}

interface PdfPageProps {
  index: number;
  page: PdfPageHandle | undefined;
  scale: number;
  rotation: number;
  register: (index: number, element: HTMLDivElement | null) => void;
  /** Whether a pinch is running, read by the observer below without re-binding. */
  gesture: RefObject<boolean>;
  /** The same fact, for the overlay, which does need to re-render on it. */
  gesturing: boolean;
  renderOverlay?: PdfStageProps['renderOverlay'];
}

function PdfPage({
  index,
  page,
  scale,
  rotation,
  register,
  gesture,
  gesturing,
  renderOverlay,
}: PdfPageProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [near, setNear] = useState(false);

  const total = normalizeRotation((page?.rotation ?? 0) + rotation);
  const displayed =
    page === undefined ? { width: 612, height: 792 } : rotatedSize(page.size, total);

  const cssWidth = Math.round(displayed.width * scale);
  const cssHeight = Math.round(displayed.height * scale);

  /**
   * Hold pixels only while the page is within reach of the viewport.
   *
   * **The first answer is measured, not waited for, and that is a bug fix
   * rather than an optimisation.** An `IntersectionObserver` delivers its first
   * callback asynchronously and — in a background tab, a page that is not
   * compositing, or an automated browser with no visible window — may not
   * deliver one at all. Seeding `near` from the observer alone therefore has a
   * failure mode where every canvas stays 0×0 and the reader sees an empty
   * document with nothing logged and nothing to retry, which is the worst shape
   * a failure can take. Reading the rect once is synchronous and cannot not
   * happen; the observer's job is to *demote* pages afterwards.
   *
   * Degrading toward "renders everything" costs memory on a long document in a
   * situation nobody is looking at. Degrading toward "renders nothing" costs
   * the feature.
   */
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;

    setNear(withinReach(box));

    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        /*
          **A gesture answers nothing, in either direction.** An
          `IntersectionObserver` sees through a transform, so magnifying the
          column moves every page's intersection rect even though no box has
          actually moved — and both answers it gives are then wrong and
          expensive. Pushed out of reach, a page zeroes its canvas, which is the
          blanking this whole change exists to remove arriving by a second
          route. Pulled *into* reach — which is what zooming out does to every
          page at once — it renders, at the scale that is about to be replaced.
          That second one is why zooming out was visibly worse than zooming in:
          measured at two full page renders in the middle of a single gesture.

          The effect below re-asks the question once the scale has settled.
        */
        if (gesture.current) return;

        setNear(entry?.isIntersecting ?? withinReach(box));
      },
      { rootMargin: RENDER_MARGIN },
    );
    observer.observe(box);

    return () => {
      observer.disconnect();
    };
    // `gesture` is a ref: listed to satisfy the rule, and stable, so the
    // observer is still bound exactly once.
  }, [gesture]);

  /**
   * Re-ask which pages are worth pixels, once the gesture has settled.
   *
   * The observer above stands down for the length of a pinch, so whatever it
   * would have said during one has to be asked for again — otherwise a page
   * scrolled into view by a zoom-out stays blank until something else happens
   * to move it.
   */
  useEffect(() => {
    const box = boxRef.current;
    if (gesturing || !box) return;

    setNear(withinReach(box));
  }, [gesturing, scale]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (page === undefined || canvas === null) return;

    if (!near) {
      // Zero the dimensions rather than dropping the element. Setting width or
      // height is what frees the backing store; on iOS Safari an unreferenced
      // canvas keeps its memory well past the point it matters.
      canvas.width = 0;
      canvas.height = 0;
      return;
    }

    const handle = page.render({
      canvas,
      scale,
      rotation: total,
      devicePixelRatio: deviceScale(
        cssWidth,
        cssHeight,
        window.devicePixelRatio,
        MAX_CANVAS_PIXELS,
      ),
    });

    handle.completed.catch(() => {
      // A page that will not draw leaves its box empty rather than taking the
      // whole document down — the rest of the filing is still readable, and
      // the frame's own error path covers a document that would not open.
    });

    return () => {
      handle.cancel();
    };
  }, [page, near, scale, total, cssWidth, cssHeight]);

  return (
    <div
      ref={(element) => {
        boxRef.current = element;
        register(index, element);
      }}
      data-page-index={index}
      // `relative`, so the overlay below positions against this page rather
      // than against the scroll container.
      className="relative shrink-0"
      // Runtime-computed geometry, not a design value — the same exemption
      // `ZoomStage`'s transform documents. Numbers, so React writes the unit.
      style={{
        width: cssWidth,
        height: cssHeight,
        background: token('bp-panel'),
        boxShadow: token('shadow-md'),
      }}
    >
      {/*
        **Sized here rather than by whatever last drew into it.** The bitmap's
        own dimensions are the renderer's business; how much room it takes up is
        this box's, and tying the two together is what left a stale canvas
        showing the wrong part of the page for a frame after every zoom. Filling
        the box means the picture is always in the right place at the right
        size, and only ever the wrong sharpness.
      */}
      <canvas ref={canvasRef} aria-hidden="true" className="block h-full w-full" />
      {/*
        Only once the page's real size is known. Before that `displayed` is the
        letter-sized guess above, and fields placed against a guess would jump
        the moment the true dimensions arrived.
      */}
      {page === undefined
        ? null
        : renderOverlay?.(index, { scale, rotation: total, page: page.size, gesturing })}
    </div>
  );
}

/**
 * Where the content box's top-left would be with no transform on it.
 *
 * **Derived from the box itself, never from `scrollTop`, and that is the fix
 * for the worst bug this component had.** The obvious version reads the scroll
 * offset each sample — but a transform contributes to a scroll container's
 * scrollable overflow, so magnifying the column changes what the browser will
 * *allow* `scrollTop` to be and it silently clamps. Shrinking it clamps hard,
 * which is why zooming out was so much worse than zooming in. The gesture then
 * accumulated against an offset that had moved underneath it, and the commit
 * landed thousands of pixels away — measured at 3324px on a 3.3x pinch.
 *
 * Scaling about a `0 0` origin leaves the box's own corner at `origin + view`,
 * so the corner the browser reports minus the translation already applied is
 * the untransformed origin, whatever the scroll has been doing.
 */
function contentOrigin(stage: HTMLElement, content: HTMLElement, view: View): Point {
  const rect = rectOnStage(stage, content);

  return { x: rect.left - view.x, y: rect.top - view.y };
}

/**
 * Whether a page is close enough to the viewport to be worth pixels.
 *
 * **Measured, not awaited.** An `IntersectionObserver` delivers its first
 * callback asynchronously and — in a background tab, a page that is not
 * compositing, or an automated browser with no visible window — may not deliver
 * one at all, which would leave every canvas 0×0 and the reader looking at an
 * empty document. Reading the rect is synchronous and cannot not happen.
 *
 * In jsdom every rect is 0×0, which lands inside this band — so a component
 * test exercises the rendering path rather than skipping it.
 */
function withinReach(box: HTMLElement): boolean {
  const rect = box.getBoundingClientRect();
  const reach = window.innerHeight * 1.5;

  return rect.bottom > -reach && rect.top < window.innerHeight + reach;
}

/** A page's box, measured from the stage's top-left rather than the viewport's. */
function rectOnStage(stage: HTMLElement, box: HTMLElement): Rect {
  const stageRect = stage.getBoundingClientRect();
  const rect = box.getBoundingClientRect();

  return {
    left: rect.left - stageRect.left,
    top: rect.top - stageRect.top,
    width: rect.width,
    height: rect.height,
  };
}

/**
 * Which page the reader is holding, and where in it.
 *
 * **The page containing the point when there is one, and the nearest otherwise.**
 * A pinch centred on the gap between two pages, or on the empty field around a
 * document zoomed out below the width of the screen, still has to resolve to
 * something — and `anchorIn` keeps the part that fell outside the page separate
 * from the part that fell inside, because only the second one scales.
 *
 * Measured against the *transformed* boxes on purpose: a fraction of a page is
 * the same number whether the column is magnified or not, which is what lets
 * this be read while a gesture is still on screen.
 */
function anchorPage(
  stage: HTMLElement,
  boxes: ReadonlyMap<number, HTMLDivElement>,
  focus: Point,
): { pageIndex: number; anchor: ReturnType<typeof anchorIn> } | undefined {
  let best: { pageIndex: number; rect: Rect; distance: number } | undefined;

  for (const [pageIndex, box] of boxes) {
    const rect = rectOnStage(stage, box);
    const above = rect.top - focus.y;
    const below = focus.y - (rect.top + rect.height);
    const distance = Math.max(0, above, below);

    if (best === undefined || distance < best.distance) best = { pageIndex, rect, distance };
    if (distance === 0) break;
  }

  if (best === undefined) return undefined;

  /*
    **Read through the live transform, and both halves survive it.** A fraction
    of a page is the same number magnified or not. The leftover pixels used to
    need dividing back down, because the gap they usually represent was a flat
    24px however far the column was magnified — but the gutter scales with the
    zoom now, precisely so the magnified column and the drawn one are the same
    shape. That makes the layout this is measured through and the layout it will
    be applied to identical in size, and the remainder passes straight through.
  */
  return { pageIndex: best.pageIndex, anchor: anchorIn(best.rect, focus) };
}

export { GUTTER };
